import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Button, Input, Spinner, Text, useToastContext } from "@ttc3k/trekker";
import { useMemo, useState } from "react";
import { Box, Flex } from "styled-system/jsx";
import { DateInput, WhereaboutsModal } from "shared/components";
import { formatDate, formatPrice } from "shared/utils";
import { EmailActivitySlideout } from "shared/components/organisms/EmailActivitySlideout/EmailActivitySlideout";
import type { ActivityLogManyQuery } from "gql/generated";
import {
  buildSyntheticPaymentHistoryEntries,
  consolidateRecordPaymentHistory,
  describeInvoiceJournalPayment,
  formatJournalChangeValue,
  formatJournalFieldLabel,
  getInvoiceJournalTimelineTimestamp,
  isDateOnlyTimestamp,
  sortInvoiceJournalEntries,
  sortPaymentEntriesNewestFirst,
} from "./invoiceJournalTimeline";

/** "July 16, 2026 7:32"; business dates without a clock time omit it. */
const formatJournalDate = (value?: string | null) => {
  if (!value) return "";
  return isDateOnlyTimestamp(value)
    ? formatDate(value, "MMMM d, yyyy")
    : formatDate(value, "MMMM d, yyyy H:mm");
};

const INVOICE_JOURNAL = gql`
  query InvoiceJournal($paymentRequestId: MongoID) {
    paymentRequestById(paymentRequestId: $paymentRequestId) {
      _id
      version
      currency
      amountPaid
      balanceDue
      creditBalance
      balanceStatus
      changeHistory {
        eventId
        action
        timestamp
        recordedAt
        effectiveAt
        performedByName
        note
        activityLogId
        changes {
          field
          before
          after
        }
        references {
          paymentEntryId
          paymentRequestId
          orderId
          refundId
          activityLogId
          reversalOfEventId
        }
        snapshot {
          grandTotal
          amountPaid
          balanceDue
          status
        }
      }
      paymentEntries {
        _id
        type
        amount
        currency
        method
        receivedDate
        accountingDate
        payer
        reference
        chequeNumber
        processorReference
        cardBrand
        cardLast4
        accountingCode
        rawAccountingCode
        accountingCodeMappingRule
        sourceSystem
        sourceRecordId
        effectiveAt
        recordedAt
        note
        refundId
        reversalOf
        reversalReason
        createdDateISO
      }
    }
  }
`;

const REVERSE_PAYMENT = gql`
  mutation InvoiceJournalReversePayment(
    $paymentRequestId: MongoID!
    $version: Int!
    $paymentEntryId: MongoID!
    $reason: String!
    $accountingDate: String
    $idempotencyKey: String
  ) {
    paymentRequestReversePayment(
      paymentRequestId: $paymentRequestId
      version: $version
      paymentEntryId: $paymentEntryId
      reason: $reason
      accountingDate: $accountingDate
      idempotencyKey: $idempotencyKey
    ) {
      _id
      version
      status
      amountPaid
      balanceDue
      creditBalance
      balanceStatus
    }
  }
`;

const JOURNAL_ACTIVITY_LOG = gql`
  query InvoiceJournalActivityLog($id: MongoID!) {
    activityLogById(_id: $id) {
      _id
      entityName {
        en
        fr
        es
      }
      message {
        en
        fr
        es
      }
      initiatedBy
      type
      createdDateISO
      entity {
        _entityId
        type
      }
      owner {
        entityId
        type
      }
      emailActivity {
        sentHtml
        templatePreviewHtml
        fromAddress {
          name
          email
        }
        subject
        recipients {
          userId
          personId
          email
          displayName
          deliveredAt
          openedAt
          clickedAt
          bouncedAt
          droppedAt
          deferredAt
          spamReportedAt
        }
      }
    }
  }
`;

const formatAction = (action?: string) => {
  if (action === "internal_note") return "Note";
  if (action === "PAYMENT_REVERSED" || action === "payment_reversed") {
    return "Payment voided";
  }
  if (action === "paid") return "Marked as paid";
  if (action === "payment_recorded") return "Payment recorded";
  return (action ?? "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map(word =>
      word === "eft" ? "EFT" : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
};

const isRedundantPaymentRecordedNote = (
  note?: string | null,
  paymentDetail?: string,
) => {
  if (!note?.trim()) return false;
  if (/^payment recorded by\b/i.test(note.trim())) return true;
  if (paymentDetail && note.trim() === paymentDetail) return true;
  return false;
};

type Props = {
  paymentRequestId?: string | null;
  /** Skip fetching while the containing modal/tab is not visible. */
  skip?: boolean;
  /** Called after a successful void, so the host can flag pending changes. */
  onVoided?: () => void | Promise<void>;
};

export const InvoiceJournalContent = ({
  paymentRequestId,
  skip = false,
  onVoided,
}: Props) => {
  const { toastFactory } = useToastContext();
  const [reversingPaymentId, setReversingPaymentId] = useState<string | null>(
    null,
  );
  const [reversalReason, setReversalReason] = useState("");
  const [reversalAccountingDate, setReversalAccountingDate] = useState("");
  const [emailActivityLog, setEmailActivityLog] = useState<
    ActivityLogManyQuery["activityLogMany"][0] | null
  >(null);
  const [fetchActivityLog] = useLazyQuery(JOURNAL_ACTIVITY_LOG);
  const { data, loading, refetch } = useQuery(INVOICE_JOURNAL, {
    variables: { paymentRequestId },
    skip: skip || !paymentRequestId,
    fetchPolicy: "network-only",
  });
  const [reversePayment, { loading: reversing }] = useMutation(REVERSE_PAYMENT);
  const invoice = data?.paymentRequestById;
  const paymentEntriesNewestFirst = useMemo(
    () => sortPaymentEntriesNewestFirst(invoice?.paymentEntries ?? []),
    [invoice?.paymentEntries],
  );
  const paymentEntriesById = useMemo(
    () =>
      new Map(
        paymentEntriesNewestFirst.map((entry: any) => [
          String(entry._id),
          entry,
        ]),
      ),
    [paymentEntriesNewestFirst],
  );
  const history = useMemo(() => {
    const changeHistory = consolidateRecordPaymentHistory(
      invoice?.changeHistory ?? [],
    );
    const syntheticPaymentEntries = buildSyntheticPaymentHistoryEntries(
      paymentEntriesNewestFirst,
      changeHistory,
    );
    const combined = [...changeHistory, ...syntheticPaymentEntries];
    return combined.length ? sortInvoiceJournalEntries(combined) : [];
  }, [invoice?.changeHistory, paymentEntriesNewestFirst]);
  const reversedPaymentIds = useMemo(
    () =>
      new Set(
        paymentEntriesNewestFirst
          .map((entry: any) => entry.reversalOf)
          .filter(Boolean)
          .map(String),
      ),
    [paymentEntriesNewestFirst],
  );

  const submitReversal = async () => {
    if (!paymentRequestId || !invoice || !reversingPaymentId) return;
    if (!reversalReason.trim()) {
      toastFactory.create({
        title: "Reason for voiding is required",
        type: "error",
      });
      return;
    }
    try {
      await reversePayment({
        variables: {
          paymentRequestId,
          version: invoice.version,
          paymentEntryId: reversingPaymentId,
          reason: reversalReason.trim(),
          accountingDate: reversalAccountingDate || undefined,
          idempotencyKey: crypto.randomUUID(),
        },
      });
      setReversingPaymentId(null);
      setReversalReason("");
      setReversalAccountingDate("");
      await refetch();
      await onVoided?.();
      toastFactory.create({ title: "Payment voided" });
    } catch (error: any) {
      toastFactory.create({
        title: "Could not void payment",
        description: error?.message,
        type: "error",
      });
    }
  };

  const openEmailActivity = async (activityLogId: string) => {
    const result = await fetchActivityLog({
      variables: { id: activityLogId },
    });
    if (result.data?.activityLogById) {
      setEmailActivityLog(
        result.data
          .activityLogById as ActivityLogManyQuery["activityLogMany"][0],
      );
    }
  };

  return (
    <>
      {loading || !invoice ? (
        <Flex justifyContent="center" py="600">
          <Spinner width="32px" height="32px" />
        </Flex>
      ) : (
        <Flex flexDir="column" gap="300">
          <Flex gap="300" flexWrap="wrap">
            <Text visual="smallSemiBold">
              Paid: ${formatPrice(invoice.amountPaid ?? 0)}
            </Text>
            <Text visual="smallSemiBold">
              Balance: ${formatPrice(invoice.balanceDue ?? 0)}
            </Text>
            {(invoice.creditBalance ?? 0) > 0 ? (
              <Text visual="smallSemiBold" color="text.warning.dark">
                Credit: ${formatPrice(invoice.creditBalance)}
              </Text>
            ) : null}
          </Flex>

          {paymentEntriesNewestFirst.length ? (
            <Flex flexDir="column" gap="100">
              <Text visual="bodySemiBold">Payments</Text>
              {paymentEntriesNewestFirst.map((entry: any) => (
                <Box
                  key={entry._id}
                  borderWidth="1px"
                  borderColor="border.light"
                  borderRadius="md"
                  p="200"
                >
                  <Flex justifyContent="space-between" gap="200">
                    <Flex flexDir="column" gap="50">
                      <Text visual="smallSemiBold">
                        {entry.type === "REVERSAL"
                          ? `Voided payment · ${formatAction(entry.method)}`
                          : entry.type === "REFUND"
                            ? `Refund · ${formatAction(entry.method)}`
                            : describeInvoiceJournalPayment(entry).title}
                      </Text>
                      <Text visual="smallRegular" color="text.mid">
                        {formatJournalDate(entry.receivedDate)}
                        {entry.type === "PAYMENT" &&
                        describeInvoiceJournalPayment(entry).detail
                          ? ` · ${describeInvoiceJournalPayment(entry).detail}`
                          : ""}
                        {entry.type !== "PAYMENT" && entry.reference
                          ? ` · ${entry.reference}`
                          : ""}
                        {entry.type !== "PAYMENT" && entry.chequeNumber
                          ? ` · Cheque ${entry.chequeNumber}`
                          : ""}
                      </Text>
                      {entry.note ? (
                        <Text visual="smallRegular" color="text.mid">
                          {entry.note}
                        </Text>
                      ) : null}
                      {entry.accountingCode || entry.rawAccountingCode ? (
                        <Text visual="smallRegular" color="text.mid">
                          GL code:{" "}
                          {entry.accountingCode ?? entry.rawAccountingCode}
                          {entry.rawAccountingCode &&
                          entry.accountingCode &&
                          entry.rawAccountingCode !== entry.accountingCode
                            ? ` (source: ${entry.rawAccountingCode})`
                            : ""}
                        </Text>
                      ) : null}
                      {entry.sourceSystem ? (
                        <Text visual="smallRegular" color="text.light">
                          Source: {entry.sourceSystem}
                          {entry.sourceRecordId
                            ? ` · ${entry.sourceRecordId}`
                            : ""}
                        </Text>
                      ) : null}
                    </Flex>
                    <Flex flexDir="column" alignItems="flex-end" gap="100">
                      <Text
                        visual="smallCode"
                        color={
                          entry.amount < 0 ? "text.error.dark" : "text.dark"
                        }
                      >
                        ${formatPrice(entry.amount)}
                      </Text>
                      {entry.type === "PAYMENT" &&
                      entry.amount > 0 &&
                      !reversedPaymentIds.has(String(entry._id)) ? (
                        <Button
                          size="sm"
                          mode="light"
                          onClick={() => setReversingPaymentId(entry._id)}
                        >
                          Void payment
                        </Button>
                      ) : null}
                    </Flex>
                  </Flex>
                </Box>
              ))}
            </Flex>
          ) : null}

          <Flex flexDir="column" gap="100">
            <Text visual="bodySemiBold">History</Text>
            {history.length ? (
              history.map((entry: any) => {
                const timelineTimestamp =
                  getInvoiceJournalTimelineTimestamp(entry);
                const linkedPaymentEntry =
                  entry.paymentEntry ??
                  (entry.references?.paymentEntryId
                    ? paymentEntriesById.get(
                        String(entry.references.paymentEntryId),
                      )
                    : undefined);
                const paymentDescription = linkedPaymentEntry
                  ? describeInvoiceJournalPayment(linkedPaymentEntry)
                  : undefined;
                const actionLower = String(entry.action ?? "").toLowerCase();
                // Voids reference the original payment entry; without this
                // check they'd inherit its "Payment recorded · …" title.
                const actionLabel =
                  actionLower === "paid" || actionLower === "payment_reversed"
                    ? formatAction(entry.action)
                    : (paymentDescription?.title ?? formatAction(entry.action));
                const showNote =
                  !!entry.note &&
                  !isRedundantPaymentRecordedNote(
                    entry.note,
                    paymentDescription?.detail,
                  );
                return (
                  <Box
                    key={entry.eventId ?? `${entry.timestamp}-${entry.action}`}
                    borderBottomWidth="1px"
                    borderColor="border.light"
                    py="150"
                  >
                    <Flex justifyContent="space-between" gap="200">
                      <Text visual="smallSemiBold">
                        {actionLabel}
                        {entry.performedByName
                          ? ` by ${entry.performedByName}`
                          : ""}
                      </Text>
                      <Text visual="smallRegular" color="text.light">
                        {formatJournalDate(timelineTimestamp)}
                      </Text>
                    </Flex>
                    {paymentDescription?.detail ? (
                      <Text visual="smallRegular" color="text.mid" mt="50">
                        {paymentDescription.detail}
                      </Text>
                    ) : null}
                    {showNote ? (
                      <Text visual="smallRegular" color="text.mid" mt="50">
                        {entry.note}
                      </Text>
                    ) : null}
                    {timelineTimestamp &&
                    entry.recordedAt &&
                    timelineTimestamp !== entry.recordedAt ? (
                      <Text visual="smallRegular" color="text.light" mt="50">
                        Recorded in Whereabouts{" "}
                        {formatJournalDate(entry.recordedAt)}
                      </Text>
                    ) : null}
                    {entry.activityLogId ? (
                      <Button
                        size="sm"
                        mode="light"
                        mt="100"
                        onClick={() =>
                          void openEmailActivity(String(entry.activityLogId))
                        }
                      >
                        View email activity
                      </Button>
                    ) : null}
                    {(entry.changes ?? []).map((change: any) => (
                      <Text
                        key={`${entry.eventId}-${change.field}`}
                        visual="smallRegular"
                        color="text.mid"
                      >
                        {formatJournalFieldLabel(change.field)}:{" "}
                        {formatJournalChangeValue(change.before)} →{" "}
                        {formatJournalChangeValue(change.after)}
                      </Text>
                    ))}
                  </Box>
                );
              })
            ) : (
              <Text visual="smallRegular" color="text.mid">
                No history recorded.
              </Text>
            )}
          </Flex>
        </Flex>
      )}

      <WhereaboutsModal
        isOpen={!!reversingPaymentId}
        onClose={() => setReversingPaymentId(null)}
        isMaxZIndex
        isOverflowVisible
        title="Void payment"
        description="Correct an invalid payment record. This does not refund money; the original remains in the audit trail and an equal negative entry is added."
        cancelButtonProps={{ onClick: () => setReversingPaymentId(null) }}
        saveButtonCopy="Void payment"
        saveButtonProps={{
          onClick: () => void submitReversal(),
          isLoading: reversing,
        }}
      >
        <Flex flexDir="column" gap="150">
          <Input
            label="Reason"
            value={reversalReason}
            onChange={event => setReversalReason(event.target.value)}
          />
          <DateInput
            title="Accounting date (optional)"
            value={reversalAccountingDate || undefined}
            onValueChange={setReversalAccountingDate}
            onClearDatesClick={() => setReversalAccountingDate("")}
            clearDatesLabel="Clear date"
            canSelectPastDates
            usePortal
            fullWidth
          />
        </Flex>
      </WhereaboutsModal>
      <EmailActivitySlideout
        isOpen={!!emailActivityLog}
        onClose={() => setEmailActivityLog(null)}
        activityLog={emailActivityLog}
        zIndex="max"
      />
    </>
  );
};
